import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  type,
  withComputed,
  withHooks,
  withProps,
} from '@ngrx/signals';
import {
  withEntities,
  entityConfig,
  EntityId,
  setEntity,
  updateEntity,
  removeEntity,
} from '@ngrx/signals/entities';
import {
  withCallStatus,
  withEntitiesLoadingCall,
  withCalls,
  withEntitiesCalls,
  callConfig,
  entityCallConfig,
  withLogger,
  withEntitiesLocalSort,
  withEntitiesRemoteFilter,
  withEntitiesMultiSelection,
  withEntitiesLocalPagination,
} from '@ngrx-traits/signals';
import {
  TextBlock,
  TextBlockView,
  toPatchOperations,
} from '../models/text-block.model';
import { TextBlockService } from '../data/text-block.service';
import { map, filter, throwError, forkJoin, of } from 'rxjs';
import { ToastService } from '@core/toast';
import { ConfirmDeleteDialogService } from '@core/confirmation';
import {
  TextBlockCategory,
  TextBlockCategoryStore,
} from '../../text-block-categories-list';
import { TextBlockArea } from 'src/app/models/enum-types.model';
import { EnumStore } from 'src/app/features/options/enum/state/enum.store';

const textBlockConfig = entityConfig({
  entity: type<TextBlock>(),
  collection: 'textBlock',
  selectId: (textBlock: TextBlock) => textBlock.id,
});

export type TextBlockFilter = {
  searchTerm: string;
  textBlockCategoryIds?: number[];
  textBlockAreaIds?: number[];
};

export const TextBlockStore = signalStore(
  withEntities(textBlockConfig),
  withCallStatus({
    prop: textBlockConfig.collection,
    initialValue: 'init',
  }),
  withEntitiesLocalSort({
    entity: type<TextBlock>(),
    collection: textBlockConfig.collection,
    defaultSort: { field: 'bezeichnung', direction: 'asc' },
  }),
  withEntitiesRemoteFilter({
    ...textBlockConfig,
    defaultFilter: {
      searchTerm: '',
      textBlockCategoryIds: undefined,
      textBlockAreaIds: undefined,
    } as TextBlockFilter,
    defaultDebounce: 300,
  }),
  withEntitiesMultiSelection({
    entity: type<TextBlock>(),
    collection: textBlockConfig.collection,
  }),
  withEntitiesLocalPagination({
    entity: type<TextBlock>(),
    collection: textBlockConfig.collection,
    pageSize: 20,
  }),
  withLogger({
    name: 'TextBlockStore',
    showDiff: true,
  }),
  withProps(() => ({
    dialogService: inject(ConfirmDeleteDialogService),
    textBlockService: inject(TextBlockService),
    _toastService: inject(ToastService),
    textBlockCategoryStore: inject(TextBlockCategoryStore),
    enumStore: inject(EnumStore),
  })),
  withComputed(({ ...store }) => ({
    textBlockCategories: computed<TextBlockCategory[]>(() =>
      store.textBlockCategoryStore.textBlockCategoryEntities()
    ),
    textBlockAreas: computed<TextBlockArea[]>(() =>
      store.enumStore.enumForKey<TextBlockArea>('TextBlockArea')
    ),
  })),
  withEntitiesLoadingCall({
    ...textBlockConfig,
    fetchEntities: ({ textBlockService, textBlockFilter }) => {
      const filter = textBlockFilter();

      return textBlockService
        .getListWithParams({
          search: filter.searchTerm || undefined,
          textBlockCategoryIds: filter.textBlockCategoryIds,
          textBlockAreaIds: filter.textBlockAreaIds,
        })
        .pipe(
          map((response) => ({
            entities: response,
            total: response.length,
          }))
        );
    },
  }),
  withComputed(
    ({ textBlockIds, textBlockEntities, textBlockCurrentPage, ...store }) => {
      const mapToView = (entity: TextBlock): TextBlockView => {
        const categories = store.textBlockCategories() ?? [];
        const areas = store.textBlockAreas() ?? [];
        const category = categories.find(
          (c) => c.id === entity.textBlockCategoryId
        );
        const area = areas.find((a) => a.id === entity.textBlockAreaId);

        return {
          ...entity,
          textBlockCategoryDescription: category?.name ?? '',
          textBlockCategorySortSequence: category?.sortSequence,
          textBlockAreaDescription: area?.description ?? '',
        } as TextBlockView;
      };

      const sortViews = (views: TextBlockView[]): TextBlockView[] => {
        return views.sort((a, b) => {
          const catA =
            a.textBlockCategorySortSequence ?? Number.MAX_SAFE_INTEGER;
          const catB =
            b.textBlockCategorySortSequence ?? Number.MAX_SAFE_INTEGER;
          if (catA !== catB) return catA - catB;

          const seqA = a.sortSequence ?? Number.MAX_SAFE_INTEGER;
          const seqB = b.sortSequence ?? Number.MAX_SAFE_INTEGER;
          if (seqA !== seqB) return seqA - seqB;

          return (a.name ?? '').localeCompare(b.name ?? '', 'de', {
            sensitivity: 'base',
          });
        });
      };

      return {
        isEmpty: computed(() => textBlockIds()?.length === 0),
        totalCount: computed(() => textBlockIds()?.length || 0),
        textBlockViews: computed(() => {
          const entities = (textBlockCurrentPage().entities ??
            []) as TextBlock[];
          return entities.filter((e): e is TextBlock => !!e).map(mapToView);
        }),
        textBlockAllViews: computed(() => {
          const entities = textBlockEntities() ?? [];
          return sortViews(
            entities.filter((e): e is TextBlock => !!e).map(mapToView)
          );
        }),
      };
    }
  ),
  withEntitiesCalls({
    ...textBlockConfig,
    calls: ({ textBlockService, _toastService, ...store }) => ({
      updateTextBlock: entityCallConfig({
        paramsSelectId: (update) => String(update.id),
        call: (update: { id: EntityId; changes: Partial<TextBlock> }) =>
          textBlockService
            .update(Number(update.id), toPatchOperations(update.changes))
            .pipe(map(() => update)),
        mapError: () => 'Fehler beim Aktualisieren des Textes',
        onSuccess: (update) => {
          patchState(
            store,
            updateEntity(
              {
                id: String(update.id),
                changes: update.changes,
              },
              textBlockConfig
            )
          );
        },
      }),
      deleteTextBlock: entityCallConfig({
        call: (id: EntityId) =>
          textBlockService.delete(String(id)).pipe(map(() => id)),
        mapError: () => 'Fehler beim Löschen des Textes',
        onSuccess: (id) => {
          patchState(store, removeEntity(id, textBlockConfig));
          _toastService.success('Textbaustein gelöscht', '');
        },
      }),
    }),
  }),
  withCalls(
    ({
      dialogService,
      textBlockService,
      deleteTextBlock,
      _toastService,
      ...state
    }) => ({
      createTextBlock: callConfig({
        call: (textBlock: Partial<TextBlock>) =>
          textBlockService.create(textBlock),
        mapError: () => 'Fehler beim Erstellen des Textes',
        onSuccess: (textBlock) => {
          patchState(
            state,
            setEntity(textBlock, { collection: textBlockConfig.collection })
          );
          _toastService.success('Textbaustein angelegt', '');
        },
      }),
      requestDeleteDialog: callConfig({
        call: (textBlockId: EntityId) =>
          dialogService
            .confirmDelete(
              'Sind Sie sicher, dass Sie diesen Textbaustein löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.'
            )
            .pipe(
              filter((confirmed) => confirmed),
              map(() => textBlockId)
            ),
        onSuccess: (textBlockId) => deleteTextBlock(textBlockId),
      }),
      requestDeleteSelectedDialog: callConfig({
        call: () => {
          const selectedIds = state.textBlockIdsSelected();
          if (selectedIds.length === 0) {
            return throwError(() => new Error('No text blocks selected'));
          }
          return dialogService.confirmDelete().pipe(
            filter((confirmed) => confirmed),
            map(() => selectedIds.map((x) => parseInt(x.toString())))
          );
        },
        onSuccess: (selectedIds) => {
          selectedIds.forEach((id: any) => deleteTextBlock(id));
          patchState(state, { textBlockIdsSelectedMap: {} });
        },
      }),
      updateTextBlockPositions: callConfig({
        call: (reorderedTextBlocks: TextBlockView[]) => {
          const updates$ = reorderedTextBlocks.map((textBlock, index) => {
            const newPosition = index + 1;

            if (textBlock.sortSequence !== newPosition) {
              return textBlockService
                .update(
                  textBlock.id,
                  toPatchOperations({ sortSequence: newPosition })
                )
                .pipe(map(() => ({ id: textBlock.id, position: newPosition })));
            }

            return of({ id: textBlock.id, position: textBlock.sortSequence });
          });

          const join$ = updates$.length ? forkJoin(updates$) : of([]);

          return join$.pipe(
            map((results) => {
              return reorderedTextBlocks.map((textBlock, index) => {
                const r = (results as { id: number; position: number }[]).find(
                  (x) => x.id === textBlock.id
                );
                return {
                  ...textBlock,
                  position: r?.position ?? index + 1,
                };
              });
            })
          );
        },

        onSuccess: (reorderedTextBlocks) => {
          reorderedTextBlocks.forEach((textBlock) => {
            patchState(
              state,
              updateEntity(
                {
                  id: textBlock.id,
                  changes: { sortSequence: textBlock.position },
                },
                textBlockConfig
              )
            );
          });
        },
      }),
    })
  ),
  withHooks(({ ...state }) => {
    return {
      onInit() {
        state.textBlockCategoryStore.setTextBlockCategoryLoading();
      },
    };
  })
);
